import React, { Component } from "react";
import * as moment from "moment";
import "moment-timezone";

import {
  ResponsiveContainer,
  LineChart,
  AreaChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
  Tooltip,
  Label
} from "recharts";

const BASE_API_ENDPOINT = "/battery-datacenter-api/api/v1";

function formatXAxis(tickItem) {
  return moment(tickItem)
    .tz(process.env.REACT_APP_PROJECT_TIMEZONE)
    .format("HH:mm");
}

class CustomTooltip extends Component {
  render() {
    const { active, payload, label, unitSymbol } = this.props;
    if (!active || !payload || !payload.length) return null;
    const val = payload[0] && payload[0].value != null ? payload[0].value : "–";
    return (
      <div className="adb-tooltip">
        {formatXAxis(parseInt(label))}
        <br />
        <strong>
          {val} {unitSymbol}
        </strong>
      </div>
    );
  }
}

class HistoricalDayChart extends Component {
  constructor(props) {
    super(props);
    this.state = {
      sensor: null,
      data: null,
      loading: true,
      error: null
    };
  }

  componentDidMount() {
    this.loadData();
  }

  componentDidUpdate(prevProps) {
    const { sensorName, year, month, day } = this.props;
    if (
      prevProps.sensorName !== sensorName ||
      prevProps.year !== year ||
      prevProps.month !== month ||
      prevProps.day !== day
    ) {
      this.loadData();
    }
  }

  async loadData() {
    const { sensorName, year, month, day } = this.props;
    this.setState({ loading: true, error: null, data: null, sensor: null });

    try {
      const sensorRes = await fetch(
        `${BASE_API_ENDPOINT}/sensor/name/${sensorName}`
      );
      if (!sensorRes.ok) {
        this.setState({
          loading: false,
          error: `Sensor info unavailable for ${sensorName}`
        });
        return;
      }
      const sensor = await sensorRes.json();

      const dataRes = await fetch(
        `${BASE_API_ENDPOINT}/reading/day/${year}/${month}/${day}/${sensorName}`
      );
      if (!dataRes.ok) {
        this.setState({
          loading: false,
          error: `No data for ${sensorName} on ${year}-${month}-${day}`
        });
        return;
      }
      const rawData = await dataRes.json();

      const qdata = [];
      for (let i = rawData.length - 1; i >= 0; i--) {
        const o = {
          unixts: parseInt(rawData[i].timestamp),
          value:
            rawData[i].value != null
              ? parseFloat(rawData[i].value.toFixed(3))
              : null
        };
        qdata.unshift(o);
      }

      this.setState({ sensor, data: qdata, loading: false });
    } catch (e) {
      this.setState({ loading: false, error: String(e) });
    }
  }

  getSettings(sensor) {
    switch (sensor.unit) {
      case "Amper":
        return {
          physicalQuantityHumanReadable: "Amper [A]",
          physicalQuantitySymbol: "A",
          lineColor: "#e84037",
          yAxisMin:
            sensor.lowestBound != null ? parseFloat(sensor.lowestBound) : "auto",
          yAxisMax:
            sensor.highestBound != null
              ? parseFloat(sensor.highestBound)
              : "dataMax + 1"
        };
      case "Volt":
        return {
          physicalQuantityHumanReadable: "Voltage [V]",
          physicalQuantitySymbol: "V",
          lineColor: "#35ba43",
          yAxisMin:
            sensor.lowestBound != null ? parseFloat(sensor.lowestBound) : "auto",
          yAxisMax:
            sensor.highestBound != null
              ? parseFloat(sensor.highestBound)
              : "dataMax + 1"
        };
      default:
        return {
          physicalQuantityHumanReadable: sensor.unit,
          physicalQuantitySymbol: sensor.unit,
          lineColor: "#555",
          yAxisMin: "auto",
          yAxisMax: "dataMax + 1"
        };
    }
  }

  render() {
    const { sensorName } = this.props;
    const { sensor, data, loading, error } = this.state;

    if (loading) {
      return (
        <div className="adb-sensor-chart">
          <div
            className="loader"
            style={{ margin: "80px auto", width: 60, height: 60, borderWidth: 8 }}
          />
        </div>
      );
    }

    if (error) {
      return (
        <div className="adb-sensor-chart">
          <h4>{sensorName}</h4>
          <div className="no-data-place-holder" style={{ lineHeight: "normal", paddingTop: 60 }}>
            {error}
          </div>
        </div>
      );
    }

    if (!sensor) return null;

    const {
      physicalQuantityHumanReadable,
      physicalQuantitySymbol,
      lineColor,
      yAxisMin,
      yAxisMax
    } = this.getSettings(sensor);

    // Require at least 90 points (same threshold as ChartComponent)
    const chartData = data && data.length >= 90 ? data : null;
    const chartMargin = { top: 0, right: 0, left: 0, bottom: 0 };

    const xAxis = (
      <XAxis
        scale="auto"
        tickCount={10}
        allowDataOverflow={false}
        interval="preserveEnd"
        dataKey="unixts"
        domain={["minData + 1", "maxData + 0"]}
        tickFormatter={ts => formatXAxis(ts)}
        type="number"
      />
    );

    const yAxis = (
      <YAxis
        scale="linear"
        domain={[yAxisMin, yAxisMax]}
        dataKey="value"
        type="number"
      >
        <Label
          value={physicalQuantityHumanReadable}
          angle={-90}
          position="insideLeft"
          opacity="0.6"
        />
      </YAxis>
    );

    const tooltip = (
      <Tooltip
        isAnimationActive={false}
        content={<CustomTooltip unitSymbol={physicalQuantitySymbol} />}
      />
    );

    const grid = <CartesianGrid strokeOpacity="0.7" strokeDasharray="3 3" />;

    const gradientId = `hdcGrad-${sensorName}`;

    const voltChart = (
      <AreaChart data={chartData} margin={chartMargin}>
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={lineColor} stopOpacity={0.6} />
            <stop offset="95%" stopColor={lineColor} stopOpacity={0} />
          </linearGradient>
        </defs>
        {xAxis}
        {yAxis}
        {grid}
        {tooltip}
        <Area
          type="monotone"
          dataKey="value"
          stroke={lineColor}
          fillOpacity={1}
          fill={`url(#${gradientId})`}
          connectNulls={false}
          strokeWidth={2}
          isAnimationActive={false}
        />
      </AreaChart>
    );

    const amperChart = (
      <LineChart data={chartData} margin={chartMargin}>
        {xAxis}
        {yAxis}
        {grid}
        <ReferenceLine y={0} stroke="black" />
        {tooltip}
        <Line
          type="monotone"
          connectNulls={false}
          dataKey="value"
          dot={false}
          stroke={lineColor}
          strokeWidth={2}
          isAnimationActive={false}
        />
      </LineChart>
    );

    return (
      <div className="adb-sensor-chart">
        <h4>{sensor.caption || sensorName}</h4>
        {chartData === null ? (
          <div className="no-data-place-holder">NO DATA AVAILABLE</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            {physicalQuantitySymbol === "V" ? voltChart : amperChart}
          </ResponsiveContainer>
        )}
      </div>
    );
  }
}

export default HistoricalDayChart;
